import React, { useEffect, useState, useCallback } from "react"
import { useSelector } from "react-redux";
import { Table, Space, Popconfirm, Button, Row, Col, Typography, Select, Input, message, Form, Tag, Modal } from "antd";
import { HomeOutlined,
    EditOutlined,
    DeleteOutlined,
    DownloadOutlined,
    UploadOutlined, PlusOutlined, CheckOutlined, CloseOutlined
} from "@ant-design/icons";
import axios from "../../axios-interceptor.js";
import { getOrgValuesFromTree } from "../../utils/extractElementsFromTree";
import BreadCrumb from "../BreadCrumb";
import exportExcel from "../../utils/exportExcel";
import { getUserSpaceEntitlements } from "../../utils/currentUserDetail";
import { useTranslation } from "react-i18next";
import UserRoleDropdown from "../UserRoleDropdown/index.js";
import { getNameFromEmail } from "../../utils/getNameFromEmail.js";
import { showLoader } from "../../utils/showloader.js";

const { Title } = Typography;

const BACKEND_API = process.env.REACT_APP_BACKEND_API;
let tenantId = localStorage.getItem("tenantId")

const EmployeeMaster = ({ title }) => {
    const { t } = useTranslation();
    let defaultBreadCrumbItems = [{title: <HomeOutlined />, path: "/app/dashboard"}, { title: t('Employee Master')}]
    const [employeeData, setEmployeeData] = useState([])
    const [selectedEmployee, setSelectedEmployee] = useState(null)
    const [formData, setFormData] = useState(null)
    const [searchKey, setSearchKey] = useState(false)
    const [designationData, setDesignationData] = useState([])
    const [structureData, setStructureData] = useState([])
    const [orgUnits, setOrgUnits] = useState([])
    const [orgValues, setOrgValues] = useState([])
    const [filteredEmployeeData, setFilteredEmployeeData] = useState([])
    const [form] = Form.useForm();
    const { userInfo } = useSelector(state => state.user);
    const adminEntitlements = getUserSpaceEntitlements(null, userInfo.userAccess);

    useEffect(() => {
        form.setFieldsValue({ ...formData });
    }, [form, formData]);

    const fetchDesignation = useCallback(async () => {
        axios({
            method: "GET",
            url: `${BACKEND_API}/designation/${tenantId}`,
            headers: {
                userId: tenantId,
            },
        })
            .then((response) => {
                setDesignationData(response.data)
            })
            .catch((error) => {
            });
    }, [])

    useEffect(() => {
        fetchDesignation();
    }, [fetchDesignation])

    const fetchStructure = useCallback(async () => {
        axios({
            method: "GET",
            url: `${BACKEND_API}/orgstructure/1`,
            headers: {
                userId: 1,
            },
        })
            .then((response) => {
                let strData = JSON.parse(response.data[0].orgStructure);
                setStructureData(strData)
            })
            .catch((error) => {
            });
    }, [])

    useEffect(() => {
        fetchStructure();
    }, [fetchStructure]);

    const fetchUsers = useCallback(async () => {
        showLoader(true)
        await axios({
            method: "GET",
            url: `${BACKEND_API}/usersByTenant?tenantId=${tenantId}`,
            headers: {
                userId: tenantId,
            },
        })
            .then((response) => {
                setEmployeeData(response.data)
                setFilteredEmployeeData(response.data)
            })
            .catch((error) => {
            });
        showLoader(false)
    }, [])

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers])

    useEffect(() => {
        if (searchKey) {
            let employees = employeeData.filter(data => data.name.toLowerCase().includes(searchKey.toLowerCase()) || data.email.toLowerCase().includes(searchKey.toLowerCase()))
            setFilteredEmployeeData(employees)
        } else {
            setFilteredEmployeeData(employeeData)
        }
    }, [searchKey])

    const onCheck = async () => {
        try {
            await form.validateFields();
            onSaveEmployee()
        } catch (errorInfo) {
        }
    };

    const onSaveEmployee = (type='save', record=formData) => {
        let currentFormData = record;
        let postData = { name: currentFormData.name, designation:currentFormData.designation, orgUnit: currentFormData.orgUnit, orgValue: currentFormData.orgValue, tenantId: currentFormData.tenantId || tenantId };
        if(!currentFormData.userId) {
            postData.defaultLanguage = "english"
            postData.image = ""
            postData.email = currentFormData.email
        } else {
            postData.userId = currentFormData.userId
        }
        if(type === 'delete') {
            postData.status = "inactive"
        }
        // return
        axios({
            method: postData.userId ? "PUT" : "POST",
            url: postData.userId ? `${BACKEND_API}/updateuser` : `${BACKEND_API}/createuser`,
            data: postData,
            headers: {
                userId: userInfo.userId
            }
        })
            .then(response => {
                if(typeof response.data === "string") {
                    message.error(response.data)
                } else {
                    fetchUsers();
                    setSelectedEmployee(null)
                    setFormData(null)
                    message.success(postData.userId ? type === 'Delete' ? t('Employee deleted successfully') : t('Employee updated successfully') : t('Employee added successfully'))
                }
            })
            .catch(error => {
                let label = formData.userId ? 'update' : 'add'
                message.error(t('Error in saving. Please try again.'))
            })
    }

    const onAllowEdit = (employee, rowIndex) => {
        let tempList = employeeData.map((data, index) => {
            if (employee.userId === data.userId) {
                return { ...data, isEditable: true }
            } else {
                return { ...data, isEditable: false }
            }
        })
        setFilteredEmployeeData(tempList)
        setSelectedEmployee({ ...employee })
        let orgUnitData = designationData.filter(data => data.designationName === employee.designation);
            orgUnitData = orgUnitData.map(data => {
                return { label: data.level, value: data.level }
            })
        setOrgUnits(orgUnitData)
        let unitValuesFromTree = getOrgValuesFromTree(structureData, employee.orgUnit)
            unitValuesFromTree = unitValuesFromTree.map(val => {
                return { label: val, value: val }
            })
        setOrgValues(unitValuesFromTree)
        setFormData({ ...employee })
    }

    const handleEditEmployee = (employee, rowIndex) => {
        let editableRows = filteredEmployeeData.filter(data => data.isEditable);
        if(editableRows.length > 0 ) {
            Modal.confirm({
                title: t('Confirm'),
                content: t("You haven't saved the current employee. Are you sure to continue?"),
                okText:t("Yes"),
                onOk:(event) => {
                    onAllowEdit(employee, rowIndex);
                    Modal.destroyAll()
                },
                footer: (_, { OkBtn, CancelBtn }) => (
                  <>
                    <CancelBtn />
                    <OkBtn />
                  </>
                ),
              });
        } else {
            onAllowEdit(employee, rowIndex)
        }
    }

    const handleDeleteEmployee = (record) => {
        form.setFieldsValue({...record})
        onSaveEmployee('delete', record)
    }

    const addNewEmployee = () => {
        setSelectedEmployee({})
        const newData = {
            key: 0,
            name: "",
            email: "",
            designation: "",
            orgUnit: "",
            orgValue: "",
            isEditable: true,
        };
        setFormData({ ...newData })
        let modEnteredData = employeeData.map((data, index) => {
            return {
                ...data,
                key: index + 1
            }
        })
        modEnteredData.unshift(newData)
        setFilteredEmployeeData([...modEnteredData]);
    }

    const changeHandler = (key, value, record) => {
        if (key === 'designation') {
            let orgUnitData = designationData.filter(data => data.designationName === value);
            orgUnitData = orgUnitData.map(data => {
                return { label: data.level, value: data.level }
            })
            setOrgUnits(orgUnitData)
        }
        if (key === 'orgUnit') {
            let unitValuesFromTree = getOrgValuesFromTree(structureData, value)
            unitValuesFromTree = unitValuesFromTree.map(val => {
                return { label: val, value: val }
            })
            setOrgValues(unitValuesFromTree)
        }
        let updatedData = filteredEmployeeData.map(data => {
            let dataObj = { ...data }
            if (data.userId === record.userId) {
                dataObj[key] = value 
                if (key === 'designation') {
                    dataObj.orgUnit = ''
                }
                if(key === 'orgUnit' ||key === 'designation') {
                    dataObj.orgValue = ''
                }
                setFormData({ ...dataObj })
            }
            return dataObj
        })
        setFilteredEmployeeData(updatedData)
    }

    const onSaveAndEditCancel = (record, rowIndex) => {
        let updatedData = [...employeeData]
        if (record.userId) {
            updatedData = updatedData.map(data => {
                let dataObj = { ...data }
                if (record.userId === data.userId) {
                    delete dataObj.isEditable
                }
                return { ...dataObj }
            })
        } else {
            updatedData = updatedData.filter(data => data.userId)
        }
        setFilteredEmployeeData(updatedData)
        setSelectedEmployee(null)
        setFormData(null)
    }

    const onSelectUser = (_selectedData, detail={userList:[]})=> {
        let curEmail = _selectedData.user[0]
        let updatedEmployee = [...filteredEmployeeData]
        if(curEmail && curEmail[0]){
            let userObj = detail.userList.find(user => user.email === curEmail[0])
            updatedEmployee = filteredEmployeeData.map(data => {
                if(!data.userId || formData.userId === data.userId) {
                    setFormData({...formData, email: userObj.email, name: userObj.name })
                    return {...data, email: userObj.email, name: userObj.name}
                } else {
                    return {...data}
                }
            })
        } else {
            updatedEmployee = filteredEmployeeData.map(data => {
                if(!data.userId || formData.userId === data.userId) {
                    setFormData({...formData, email: "", name: "" })
                    return {...data, email: "", name: ""}
                } else {
                    return {...data}
                }
            })            
        }
        setFilteredEmployeeData(updatedEmployee)
    }

    const tableColumns = [
        {
            title: t("Email"),
            dataIndex: "email",
            key: "Email",
            width: "20%",
            render: (text, record) => {
                return (
                    record.isEditable && !record.userId ?
                        <Form.Item name="email" rules={[
                            { required: true, message: t('Email is required') }
                        ]} >
                            <UserRoleDropdown mode="SINGLE" fullDetailOnSelect={true} onSelect={onSelectUser} selectedOptions={{user:(record && record.email) ? [record.email] : [], group:[], role:[]}} placeholderElement={(record && record.email) ? "" : <div style={{color:"black"}}>{t('Employee email')}</div>} tabsRequired={["user"]} headerStyle={{borderColor:"transparent", background: "transparent", paddingRight:0, paddingLeft: 0, display:"flex", justifyContent:"space-between", color:"black", cursor:"pointer"}}/>

                            {/* <Input value={text} type="text" placeholder={t("Email")} onChange={(e) => changeHandler("email", e.target.value, record)} /> */}
                        </Form.Item>
                        : text
                )
            }
        },
        {
            title: t("Employee Name"),
            dataIndex: "name",
            key: "Employee Name",
            width: "15%",
            render: (text, record) => {
                return (
                    record.isEditable ?
                        <Form.Item name="name" rules={[
                            { required: true, whitespace: true, message: t('Name is required') }
                        ]} >
                            <Input value={text} type="text" placeholder={t("Employee Name")} onChange={(e) => changeHandler("name", e.target.value, record)} />
                        </Form.Item>
                        : text
                )
            }
        },        
        {
            title: t("Designation"),
            dataIndex: "designation",
            key: "Designation",
            width: "15%",
            render: (text, record) => {
                return (
                    record.isEditable ?
                        <Form.Item name="designation" rules={[
                            { required: true, message: t('Designation is required') }
                        ]} >
                            <Select placeholder={t("Select designation")} value={record.designation || "Select Designation"} onChange={(value) => changeHandler("designation", value, record)}
                                options={designationData.map(data => {
                                    return { label: data.designationName, value: data.designationName }
                                })}
                                style={{ width: "100%" }}
                            />
                        </Form.Item>
                        : <Tag bordered={false} color="processing">{text}</Tag>
                )
            }
        },
        {
            title: t("Organisation Unit"),
            dataIndex: "orgUnit",
            key: "Organisation Unit",
            width: "15%",
            render: (text, record) => {
                return (
                    record.isEditable ?
                        <Form.Item name="orgUnit" rules={[
                            { required: true, message: t('Organisation unit is required') }
                        ]} >
                            <Select placeholder={t("Select")} value={record.orgUnit || "Select"} onChange={(value) => changeHandler("orgUnit", value, record)}
                                options={orgUnits}
                                style={{ width: "100%" }}
                            />
                        </Form.Item>
                        : text
                )
            }
        },
        {
            title: t("Organisation Value"),
            dataIndex: "orgValue",
            key: "orgValue",
            width: "15%",
            render: (text, record) => {
                return (
                    record.isEditable ?
                        <Form.Item name="orgValue" rules={[
                            { required: true, message: t('Organisation value is required') }
                        ]} >
                            <Select placeholder={t("Select")} value={record.orgValue || "Select"} onChange={(value) => changeHandler("orgValue", value, record)}
                                options={orgValues}
                                style={{ width: "100%" }}
                            />
                        </Form.Item>
                        : text
                )
            }
        },
        {
            title: t("Status"),
            dataIndex: "status",
            key: "status",
            width: "10%", 
        }        
    ]
    if(adminEntitlements.includes("Employee_Master_Update")) {
        tableColumns.push(
            {
                title: t("Action"),
                dataIndex: "",
                key: "",
                width: "5%",
                render: (text, record, rowIndex) => {
                    return (
                        !record.isEditable ?
                            <Space>
                                <Button disabled={record.status === 'inactive'}
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => handleEditEmployee(record, rowIndex)}
                                />
                                <Popconfirm
                                    title={t("Delete the employee")}
                                    description={t("Are you sure to delete this employee?")}
                                    okText={t("Yes")}
                                    cancelText={t("No")}
                                    onConfirm={() => handleDeleteEmployee(record, rowIndex)}
                                >
                                    <Button disabled={record.status === 'inactive'} size="small" icon={<DeleteOutlined />} />
                                </Popconfirm>
                            </Space>
                            : <Space>
                                <Button type="primary" size="small" onClick={() => onCheck(record)} icon={<CheckOutlined />}></Button>
                                <Button size="small" onClick={() => onSaveAndEditCancel(record, rowIndex)} icon={<CloseOutlined />}></Button>
                            </Space>
                    )
                },
            }
        )
    }
    const onSearchUsers = async (value) => {
        if (value) {
            setSearchKey(value);
        } else {
            setSearchKey(false);
        }
    };

    const downloadExcel = () => {
        let columnsToDownload = tableColumns.filter(col => col.title !== "Action");
        exportExcel(columnsToDownload, filteredEmployeeData, "employee")
    }

    return (
        <div className="page-wrapper">
            <BreadCrumb items={defaultBreadCrumbItems} marginb="24px" />

            <Row
                gutter={8}
                style={{
                    margin: "0",
                    paddingBottom: "20px",
                    borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                }}
            >
                <Col span={12}>
                    <Title level={3} style={{ margin: 0 }}>
                        {t("Employee Master")}
                    </Title>
                </Col>
                <Col span={12} style={{ textAlign: "right" }}>
                    <Space>
                        <Button icon={<DownloadOutlined />} onClick={downloadExcel}>{t("Download Sheet")}</Button>
                        {
                            adminEntitlements.includes("Employee_Master_Update") ? 
                            <>
                                {/* <Button icon={<UploadOutlined />} >{t("Upload Sheet")}</Button> */}
                                <Button type="primary" icon={<PlusOutlined />} onClick={addNewEmployee}>{t("Add Employee")}</Button>
                            </>
                            : null
                        }
                            
                    </Space>
                </Col>
            </Row>
            <Row gutter={8} style={{ marginBottom: "10px", marginTop: "25px" }}>
                <Col span={4}>
                    <Input.Search
                        type="search"
                        allowClear
                        placeholder={t("Search a member")}
                        onChange={(e) => setSearchKey(e.target.value)}
                        onSearch={onSearchUsers}
                    />
                </Col>
            </Row>
            <div className="organisation-structure">
                <div style={{ width: "100%", marginTop: "20px" }}>
                    <Form form={form}>
                        <Table dataSource={filteredEmployeeData} columns={tableColumns} pagination={{ position:["bottomLeft"]}} />
                    </Form>
                </div>

                {/* {
                    selectedEmployee && <AddEditEmployee employee={selectedEmployee} onSave={onSaveEmployee} onCancel={onSaveAndEditCancel} />
                } */}
            </div>
        </div>
    )

}
export default EmployeeMaster